import React, { useState, useContext, useEffect, useRef } from 'react';
import { Box, Collapse, Paper, Tabs, Tab, Typography } from '@mui/material';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import Customers from '../components/Customers';
import Seeds from '../components/Seeds';
import Trays from '../components/Trays';
import Media from '../components/Media';
import Solution from '../components/Solution';
import Farm from '../components/Farm';
// import Overheads from '../components/Overheads';
import { trackFeature } from '../utils/analytics';
import { SnackbarContext } from '../SnackbarContext';

// A simple TabPanel component for MUI tabs.
function TabPanel(props) {
  const { children, value, index, ...other } = props;
  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`management-tabpanel-${index}`}
      aria-labelledby={`management-tab-${index}`}
      {...other}
    >
      {value === index && <Box sx={{ p: 1, mt: 2}}>{children}</Box>}
    </div>
  );
}



// Accessibility props helper.
function a11yProps(index) {
  return {
    id: `management-tab-${index}`,
    'aria-controls': `management-tabpanel-${index}`,
  };
}

const tabKeys = ['customers', 'inventory', 'farm'];

function Management() {
  const [tabValue, setTabValue] = useState(0);
  const [showGrowingReferences, setShowGrowingReferences] = useState(false);
  const { showSuccess, showError } = useContext(SnackbarContext);

  // keep track if we've already fired once
  const hasLoaded = useRef(false);

  useEffect(() => {
    if (!hasLoaded.current) {
      // skip on first render
      hasLoaded.current = true;
      return;
    }
    // only fire when the user actually switches tabs
    trackFeature('page_view', {
      page_path: `/management/${tabKeys[tabValue]}`,
      tab: tabKeys[tabValue],
    });
  }, [tabValue]);

  const handleTabChange = (_, newValue) => {
    setTabValue(newValue);
  };


  return (
    <Box
      sx={{
        maxWidth: '95%',
        backgroundColor: (theme) => theme.palette.background.default,
        color: (theme) => theme.palette.text.primary,
        minHeight: '100vh',
        display: 'flex',
        flexDirection: 'column',
        margin: '0 auto',
        padding: '0px',
        
        '&::before': {
          content: '""',
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: (theme) => theme.palette.background.default,
          zIndex: -1,
        },
      }}
    >
      <Box sx={{ 
        margin: '0 auto 0 auto' }}>
        <Tabs
          value={tabValue}
          onChange={handleTabChange}
          textColor="inherit"
          variant="standard"
          centered
          sx={{
            color: (theme) => theme.palette.tertiary.light,
            '& .MuiTabs-indicator': {
              backgroundColor: (theme) => theme.palette.warning.light,
            },
            '& .MuiTabs-flexContainer': {
              gap: 2,
            },
          }}
        >
          <Tab
            label="Customers"
            sx={{
              color: (theme) => theme.palette.primary.light,
              minWidth: 140,
              px: 2.5,
              '&.Mui-selected': {
                color: (theme) => theme.palette.warning.main,
              },
            }}
            {...a11yProps(0)}
          />
          <Tab
            label="Resources"
            sx={{
              color: (theme) => theme.palette.primary.light,
              minWidth: 140,
              px: 2.5,
              '&.Mui-selected': {
                color: (theme) => theme.palette.warning.main,
              },
            }}
            {...a11yProps(1)}
          />
          {/* <Tab
            label="Costs & Overheads"
            sx={{
              color: (theme) => theme.palette.primary.light,
              '&.Mui-selected': {
                color: (theme) => theme.palette.warning.main,
              },
            }}
            {...a11yProps(2)}
          /> */}
                    <Tab
            label="Farm"
            sx={{
              color: (theme) => theme.palette.primary.light,
              minWidth: 140,
              px: 2.5,
              '&.Mui-selected': {
                color: (theme) => theme.palette.warning.main,
              },
            }}
            {...a11yProps(2)}
          />
        </Tabs>
        
      </Box>

      <TabPanel value={tabValue} index={0}>
        <Customers setError={showError} setSuccess={showSuccess} />
      </TabPanel>

      <TabPanel value={tabValue} index={1}>
        <Box sx={{ width: '100%', maxWidth: 1400, mx: 'auto' }}>
          <Box sx={{ mb: 4 }}>
            <Box sx={{ mb: 2 }}>
              <Typography variant="h5" sx={{ fontWeight: 600 }}>
                Core Resources
              </Typography>
              <Typography variant="caption" sx={{ opacity: 0.7 }}>
                Store the core resources used throughout MicGro.
              </Typography>
            </Box>
            <Paper variant="outlined" sx={{ p: 2 }}>
              <Seeds setError={showError} setSuccess={showSuccess} />
              <Trays setError={showError} setSuccess={showSuccess} />
            </Paper>
          </Box>

          <Box>
            <Box
              onClick={() => setShowGrowingReferences((v) => !v)}
              role="button"
              tabIndex={0}
              aria-expanded={showGrowingReferences}
              onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                  setShowGrowingReferences((v) => !v);
                }
              }}
              sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                gap: 1.5,
                cursor: 'pointer',
                userSelect: 'none',
                px: 2,
                py: 1.5,
                mb: 2,
                borderRadius: 1.5,
                backgroundColor: 'action.hover',
                '&:hover': { backgroundColor: 'action.selected' },
                '&:focus-visible': {
                  outline: '2px solid',
                  outlineColor: 'primary.main',
                },
              }}
            >
              <Box sx={{ minWidth: 0 }}>
                <Typography variant="h5" sx={{ fontWeight: 600 }}>
                  Growing References
                </Typography>
                <Typography variant="caption" sx={{ opacity: 0.7 }}>
                  Optionally store media and nutrient solutions for use in harvests and presets.
                </Typography>
              </Box>
              <KeyboardArrowDownIcon
                sx={{
                  transition: 'transform 150ms ease',
                  transform: showGrowingReferences ? 'rotate(180deg)' : 'rotate(0deg)',
                }}
              />
            </Box>

            <Collapse in={showGrowingReferences}>
              <Paper variant="outlined" sx={{ p: 2 }}>
                <Media setError={showError} setSuccess={showSuccess} />
                <Solution setError={showError} setSuccess={showSuccess} />
              </Paper>
            </Collapse>
          </Box>
        </Box>
      </TabPanel>

      {/* <TabPanel value={tabValue} index={2}>
        <Overheads setError={showError} setSuccess={showSuccess} />
      </TabPanel> */}

      <TabPanel value={tabValue} index={2}>
        <Farm setError={showError} setSuccess={showSuccess} />
      </TabPanel>
    </Box>

  );

}

export default Management;

function isSubscriptionActive(userData) {
  if (!userData || !userData.accessExpires) {
    // If no expiration is set, assume active
    return true;
  }
  const now = new Date();
  const expires = userData.accessExpires.toDate
    ? userData.accessExpires.toDate()
    : new Date(userData.accessExpires);
  return now <= expires;
}
